import PropTypes from "prop-types";

import { FormInput, FormSelect } from "components/BaseForm";
import { formatPrice, selectablePrices } from "routes/product/pricing/pricing";

/**
 * Amount on a priced line: locked when the product has one enabled price, a
 * dropdown of the enabled prices otherwise. A line whose price was flagged on
 * save always gets the dropdown, even with a single enabled price.
 */
export const PricedAmountField = ({
  index,
  line,
  product,
  isFlagged,
  onPriceChange,
}) => {
  const enabledPrices = selectablePrices(product);
  if (enabledPrices.length <= 1 && !isFlagged) {
    return (
      <FormInput
        name={`invoices.${index}.amount`}
        aria-label={`Invoice line ${index + 1} amount`}
        prefix="$"
        disabled
      />
    );
  }

  const options = enabledPrices.map(price => ({
    key: price.id,
    value: price.id,
    text: `${formatPrice(price.amount)}${price.is_current ? " (Current)" : ""}`,
  }));
  // A saved line keeps showing its stored price until the user picks another.
  if (
    line.product_price_id != null &&
    !options.some(option => option.value === line.product_price_id)
  ) {
    options.push({
      key: line.product_price_id,
      value: line.product_price_id,
      text: `${formatPrice(line.amount)} (Disabled)`,
    });
  }

  return (
    <FormSelect
      name={`invoices.${index}.product_price_id`}
      aria-label={`Invoice line ${index + 1} price`}
      options={options}
      placeholder="Select a price"
      onValueChange={onPriceChange}
    />
  );
};

/**
 * Term on a priced line: locked when the product allows one term, a dropdown
 * of the allowed terms otherwise or when the stored term was flagged on save.
 */
export const AllowedTermField = ({
  index,
  line,
  product,
  isFlagged,
  onTermChange,
}) => {
  const allowedTerms = product.allowed_terms ?? [];
  if (allowedTerms.length <= 1 && !isFlagged) {
    return (
      <FormInput
        name={`invoices.${index}.term`}
        aria-label={`Invoice line ${index + 1} term`}
        disabled
      />
    );
  }

  const options = allowedTerms.map(term => ({
    key: term,
    value: String(term),
    text: String(term),
  }));
  if (line.term && !allowedTerms.includes(parseInt(line.term, 10))) {
    options.push({
      key: `stored-${line.term}`,
      value: String(line.term),
      text: `${line.term} (Not allowed)`,
    });
  }

  return (
    <FormSelect
      name={`invoices.${index}.term`}
      aria-label={`Invoice line ${index + 1} term`}
      options={options}
      placeholder="Select a term"
      onValueChange={onTermChange}
    />
  );
};

const fieldPropTypes = {
  index: PropTypes.number.isRequired,
  line: PropTypes.object.isRequired,
  product: PropTypes.shape({
    prices: PropTypes.array,
    allowed_terms: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  isFlagged: PropTypes.bool.isRequired,
};

PricedAmountField.propTypes = {
  ...fieldPropTypes,
  onPriceChange: PropTypes.func.isRequired,
};

AllowedTermField.propTypes = {
  ...fieldPropTypes,
  onTermChange: PropTypes.func.isRequired,
};
